import React, { FC, useCallback } from 'react'

import { styled } from '@linaria/react'

import { keyboardViewHeight } from 'components/block/KeyboardControl/constant'
import { KeyboardView } from 'components/block/KeyboardControl/KeyboardView'
import { useNavigateCoupleMatching } from 'components/block/Matching/hooks/useNavigateCoupleMatching'
import { Routes } from 'components/block/Profile/ProfileMain/Routes'
import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { ContainerDesktopHeight } from 'components/designSystem/shared/containerDesktopHeight'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { DevStats } from './block/DevStats'
import { LikeAnimation } from './block/LikeAnimation/LikeAnimation'
import { Rollback } from './block/Rollback'
import { GQLRatingQuery } from './gql/__generated__/Rating'
import { useCreateQueueWithCounter } from './hooks/useCreateQueueWithCounter'
import { useLikeAnimation } from './hooks/useLikeAnimation'
import { useSaveStorage } from './hooks/useSaveStorage'
import { Direction } from './Rating.types'
import { RatingNotice } from './RatingCard/RatingNotice'
import { RatingProfile } from './RatingCard/RatingProfile'
import { RatingItemType } from '../PhotoRating/PhotoRating.constants'

export const RATING_LAYOUT_ID = 'rating-main' as const

export const RatingQueue: FC<{
  ui: GQLRatingQuery['ui']
}> = ({ ui }) => {
  useSaveStorage()
  useNavigateCoupleMatching()

  const photosWithPromo = useCreateQueueWithCounter()
  const { animations, addAnimation } = useLikeAnimation()

  const { mobile } = useShallowEqualSelector(({ systemView: { mobile } }) => ({
    mobile,
  }))

  const handleVoteEvent = useCallback(
    (event: Direction, key: number) => {
      if (event === Direction.right && mobile) {
        /**
         * Анимацию like добавляем только для мобильных версий
         */
        addAnimation(key)
      }
    },
    [addAnimation, mobile]
  )

  return (
    <Parent id={RATING_LAYOUT_ID}>
      <Body>
        {photosWithPromo.reverse().map((item, index: number) => {
          if (item.type === RatingItemType.promo) {
            return (
              <RatingNotice
                key={item.key}
                promoName={item.name}
                enable={index === photosWithPromo.length - 1}
              />
            )
          }

          return (
            <RatingProfile
              key={item.photoOwner.id}
              profile={item.photoOwner}
              mainPhoto={item.photo}
              extraPhotos={item.extraPhotos}
              restPhotos={item.photoOwner.photos.photos.nodes}
              ui={ui!}
              onVoteEvent={handleVoteEvent}
              enable={index === photosWithPromo.length - 1}
            />
          )
        })}
        {animations.map((item) => (
          <LikeAnimation key={item} />
        ))}
        <Routes />
      </Body>
      {!mobile && <KeyboardView />}
      <Rollback />
      {!process.env.production && process.env.browser && false && <DevStats />}
    </Parent>
  )
}
/**
 * TODO, нельзя ставить анимацию появление или исчезание
 * из-за того что animation меняет z stacking context
 * из-зв этого ломается на mobile header footer
 */
const Parent = styled.div`
  display: grid;
  position: relative;
  flex-grow: 1;

  @media screen and (min-width: ${breakpoints.mobile + 1}px) {
    grid-auto-flow: row;
    grid-template-rows: 1fr auto;
    max-height: calc(
      ${ContainerDesktopHeight.tablet}px + ${keyboardViewHeight}px
    );
  }

  @media screen and (min-width: ${breakpoints.middleDesktop}px) {
    max-height: calc(
      ${ContainerDesktopHeight.middleDesktop}px + ${keyboardViewHeight}px
    );
  }

  @media screen and (min-width: ${breakpoints.largeDesktop}px) {
    max-height: calc(
      ${ContainerDesktopHeight.largeDesktop}px + ${keyboardViewHeight}px
    );
  }
`

const Body = styled.div`
  display: flex;
  position: relative;
`
